import { useContext, useEffect, useRef, useState } from 'react';
import { Breadcrumbs, Typography, Box, TextField, Modal, InputLabel, Select, MenuItem, FormControl, FormHelperText, TextareaAutosize } from '@mui/material';

import { Container, MainContainer } from '../../../../components';
import { Menu } from '../Menu/Menu';
import RadiusButton from '../../../../components/RadiusButton';
import dayjs from '../../../../config/dayjs';
import axios from 'axios';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { getBiometricVerifiedCandidatesCount, getCandidateBiometricDetails, verifyThumbImpressionById } from '../../../../services/candidateService';
import { getById } from '../../../../services/usersService';
import { getById as getDriveDetailsById } from '../../../../services/driveService';
import { useToaster } from '../../../../providers/ToasterProvider';
import { ProjectContext } from '../../../../providers/projectProvider';

import ARROW from '../../../../assets/images/breadcrumb-arrow.svg';
import ARROW_OPEN from '../../../../assets/images/breadcrumb-arrow-open.svg';
import THUMB_VERIFIED_ICON from '../../../../assets/images/thumb-verified.svg';
import storage from '../../../../config/storage';
import { LoadingButton } from '@mui/lab';


const contentUserSchema = z.object({
    rollNo: z.string().min(1, { message: 'Roll number is required' }),
});


export const BiometricEnrollment = () => {

    const user = storage.getUserDetails();

    const { addNotification } = useToaster();
    const { projectDetails } = useContext(ProjectContext);

    const [driveDetails, setDriveDetails] = useState({
        id: 0,
        driveName: '',
    });
    const [biometricVerifiedCandidatesCount, setBiometricVerifiedCandidatesCount] = useState(0);
    const [biometricMethods, setBiometricMethods] = useState([]);
    const [candidateDetails, setCandidateDetails] = useState(null);

    const [isVerified, setIsVerified] = useState(false);
    const [thumbModal, setThumbModal] = useState(false);
    const [thumbId, setThumbId] = useState('NONE');
    const [capturedThumbNames, setCapturedThumbNames] = useState([]);
    const [verifiedThumbNames, setVerifiedThumbNames] = useState([]);
    const defaultThumb = { thumbName: 'None', thumbKey: 'NONE', isCaptured: false, isVerified: false };
    const [capturedThumbs, setCapturedThumbs] = useState([defaultThumb]);
    const [thumbs, setThumbs] = useState([
        { thumbName: 'RH Thumb', thumbKey: 'RH_THUMB' },
        { thumbName: 'RH Index', thumbKey: 'RH_INDEX' },
        { thumbName: 'RH Middle', thumbKey: 'RH_MIDDLE' },
        { thumbName: 'RH Ring', thumbKey: 'RH_RING' },
        { thumbName: 'RH Little', thumbKey: 'RH_LITTLE' },
        { thumbName: 'LH Thumb', thumbKey: 'LH_THUMB' },
        { thumbName: 'LH Index', thumbKey: 'LH_INDEX' },
        { thumbName: 'LH Middle', thumbKey: 'LH_MIDDLE' },
        { thumbName: 'LH Ring', thumbKey: 'LH_RING' },
        { thumbName: 'LH Little', thumbKey: 'LH_LITTLE' },
        { thumbName: 'Other', thumbKey: 'OTHER' },
    ]);
    const [selectedThumb, setSelectedThumb] = useState(defaultThumb);
    const [isVerifyingThumb, setIsVerifyingThumb] = useState(false);

    const [thumbIdTouched, setThumbIdTouched] = useState(false);
    const [thumbRemarks, setThumbRemarks] = useState('');
    const [thumbRemarksTouched, setThumbRemarksTouched] = useState(false);
    const [thumbErrors, setThumbErrors] = useState({
        thumb: null,
        remarks: null,
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        trigger,
    } = useForm({
        resolver: zodResolver(contentUserSchema),
    });

    useEffect(() => {
        const fetchRequiredData = async () => {
            try {
                const res = await getById(user.id);
                if (res.code === 'OK') {
                    const driveId = res.data.driveId;
                    const driveDetailsRes = await getDriveDetailsById(driveId);
                    if (driveDetailsRes.code === 'OK') {;
                        setDriveDetails(driveDetailsRes.data);
                        const biometricVerifiedResult = await getBiometricVerifiedCandidatesCount(driveId);
                        setBiometricVerifiedCandidatesCount(biometricVerifiedResult.data.biometricVerifiedCandidatesCount || 0);
                    }
                }
            } catch (error) {
                const code = error?.response?.data?.code;
                if (code === 'CONFIGURATION_NOT_FOUND') {
                    addNotification('Exam not configured.', 'danger');
                }
                console.log('There is an error while fetching biometric methods', error);
            }
        };
        fetchRequiredData();
    }, []);

    const adjustCapturedAndVerifiedThumbs = (candidateThumbs, candidateVerifiedThumbs) => {
        const capturedNames = [];
        const verifiedNames = [];
        let candidateVerified = false;
        const candidateCapturedThumbs = [defaultThumb];
        for (let thumb of thumbs) {
            const verifiedThumb = candidateVerifiedThumbs.find(vt => vt.thumbKey === thumb.thumbKey && vt.isVerified);
            if (verifiedThumb) {
                candidateVerified = true;
                verifiedNames.push(verifiedThumb.thumbName);
                if (verifiedThumb.thumbKey === 'OTHER') {
                    candidateCapturedThumbs.push({
                        ...thumb,
                        isVerified: verifiedThumb?.isVerified || false,
                    });
                }
            }

            const capturedThumb = candidateThumbs.find(ct => ct.thumbKey === thumb.thumbKey);
            if (capturedThumb) {
                capturedNames.push(capturedThumb.thumbName);
                candidateCapturedThumbs.push({
                    ...capturedThumb,
                    isVerified: verifiedThumb ? true : false,
                });
            }
            if (!capturedThumb && !verifiedThumb && thumb.thumbKey === 'OTHER') {
                candidateCapturedThumbs.push(thumb);
            }
        }
        setCapturedThumbNames(capturedNames);
        setVerifiedThumbNames(verifiedNames);
        setCapturedThumbs(candidateCapturedThumbs);
        setIsVerified(candidateVerified);
    }

    const handleViewData = async ({ rollNo }) => {
        if (!driveDetails?.id) {
            console.log('Drive details not found');
            return;
        }
        try {
            const body = {
                driveId: driveDetails.id,
                rollNo: rollNo,
            }
            const res = await getCandidateBiometricDetails(body);
            if (res.code === 'OK') {
                setBiometricMethods(res.data.biometricMethod || []);
                setBiometricVerifiedCandidatesCount(res.data.biometricVerifiedCandidatesCount || 0);
                let dob = null;
                let examDate = null;
                if (res.data.dob) {
                    dob = dayjs(res.data.dob);
                }
                if (res.data.examDate) {
                    examDate = dayjs(res.data.examDate);
                }
                setCandidateDetails({
                    ...res.data,
                    dob: dob,
                    examDate: examDate
                });
                const capturedThumbImpressions = res.data.capturedThumbImpressions || [];
                const verifiedThumbImpressions = res.data.verifiedThumbImpressions || [];
                adjustCapturedAndVerifiedThumbs(capturedThumbImpressions, verifiedThumbImpressions);
            }
        } catch (error) {
            console.log('There is an error while fetching candidate data', error);
            const code = error?.response?.data?.code;
            if (code === 'E_NOT_FOUND') {
                addNotification('Candidate with specified roll no not found.', 'danger');
            } else if (code === 'CANDIDATE_NOT_SHORTLISTED') {
                addNotification('Candidate is not shortlisted for biometric verification.', 'warning');
            }
        }
    }

    const handleThumbModalOpen = async () => {
        if (!candidateDetails) {
            addNotification('Please select candidate to proceed.', 'warning');
            return;
        }
        setThumbModal(true);
    }

    const handleThumbModalClose = () => {
        setThumbModal(false);
        setThumbId('NONE');
        setThumbRemarks('');
        setSelectedThumb(defaultThumb);
        setThumbIdTouched(false);
        setThumbRemarksTouched(false);
        setThumbErrors({
            thumb: null,
            remarks: null,
        });
    }
    useEffect(() => {
        updateThumbErrors();
    }, [thumbId, thumbRemarks]);

    const updateThumbErrors = () => {
        let thumbErr = null;
        if (thumbIdTouched && thumbId === 'NONE') {
            thumbErr = 'Please select a thumb.';
        }
        let remarksErr = null;
        if (thumbRemarksTouched && thumbId === 'OTHER' && !thumbRemarks?.trim()) {
            remarksErr = 'Please enter remarks for other.';
        }
        setThumbErrors({
            thumb: thumbErr,
            remarks: remarksErr,
        });
    }

    const handleSaveVerifyThumb = async () => {
        if (!thumbIdTouched || !thumbRemarksTouched) {
            let thumbErr = null;
            if (thumbId === 'NONE') {
                thumbErr = 'Please select a thumb.';
            }
            let remarksErr = null;
            if (thumbId === 'OTHER' && !thumbRemarks?.trim()) {
                remarksErr = 'Please enter remarks for other.';
            }
            if (thumbErr || remarksErr) {
                setThumbIdTouched(true);
                setThumbRemarksTouched(true);
                setThumbErrors({
                    thumb: thumbErr,
                    remarks: remarksErr,
                });
                return;
            }
        }
        if (thumbErrors.thumb || thumbErrors.remarks) return;
        const selectedThumbData = capturedThumbs.find(thumb => thumb.thumbKey === thumbId);
        if (!selectedThumbData) {
            console.log('Selected thumb not found in captured thumbs');
            return;
        }

        setIsVerifyingThumb(true);
        try {
            const thumbData = {
                thumbName: selectedThumbData.thumbName,
                thumbKey: selectedThumbData.thumbKey,
                data: {},
                remarks: thumbRemarks.trim(),
            }
            const response = await verifyThumbImpressionById(candidateDetails.id, thumbData);
            if (response.code === 'OK') {
                handleViewData({ rollNo: candidateDetails.rollNo });
                addNotification('Biometric thumb verified successfully.', 'success');
                handleThumbModalClose();
            }
        } catch (error) {
            addNotification('Failed to capture biometric thumb. Please try again later.', 'danger');
            console.error('Error capturing biometric thumb:', error);
        } finally {
            setIsVerifyingThumb(false);
        }
    }

    const handleVerifyThumb = async () => {
        if (!thumbIdTouched || !thumbRemarksTouched) {
            let thumbErr = null;
            if (thumbId === 'NONE') {
                thumbErr = 'Please select a thumb.';
            }
            let remarksErr = null;
            if (thumbId === 'OTHER' && !thumbRemarks?.trim()) {
                remarksErr = 'Please enter remarks for other.';
            }
            if (thumbErr || remarksErr) {
                setThumbIdTouched(true);
                setThumbRemarksTouched(true);
                setThumbErrors({
                    thumb: thumbErr,
                    remarks: remarksErr,
                });
                return;
            }
        }
        if (thumbErrors.thumb || thumbErrors.remarks) return;

        const selectedThumbData = capturedThumbs.find((ct) => ct.thumbKey === thumbId);
        if (!selectedThumbData?.thumbKey || !selectedThumbData?.data) {
            console.log('Selected thumb has invalid data');
            return;
        }
        if (selectedThumbData.isVerified) {
            addNotification('Selected thumb is already verified.', 'information');
            return;
        }

        const thumbTemplate = selectedThumbData.data.templateBase64;
        if (!thumbTemplate) {
            console.log('No thumb template found for the selected finger.');
            return;
        }

        setIsVerifyingThumb(true);
        try {
            const res = await axios.get(`${process.env.REACT_APP_BIOMETRIC_CAPTURE_API_URL}/GetEnrollTmpl?MatchTmpl=${encodeURIComponent(thumbTemplate.toString())}`);

            if (res.data.matchSuccess !== true) {
                addNotification('Biometric verification failed:', 'danger');
            } else {
                const thumbData = {
                    thumbName: selectedThumbData.thumbName,
                    thumbKey: selectedThumbData.thumbKey,
                    data: res.data,
                    remarks: thumbRemarks.trim(),
                }
                const response = await verifyThumbImpressionById(candidateDetails.id, thumbData);
                if (response.code === 'OK') {
                    handleViewData({ rollNo: candidateDetails.rollNo });
                    addNotification('Biometric thumb verified successfully.', 'success');
                    handleThumbModalClose();
                }
            }
        } catch (error) {
            addNotification('Failed to capture biometric thumb. Please try again later.', 'danger');
            console.error('Error capturing biometric thumb:', error);
        } finally {
            setIsVerifyingThumb(false);
        }
    }

    return (
        <MainContainer>
            <Menu>
                <Breadcrumbs aria-label="breadcrumb" separator='' sx={{ gap: 12 }}>
                    <Typography color="text.primary" sx={{
                        fontWeight: 400,
                        textTransform: 'capitalize'
                    }}>
                        {projectDetails?.projectName ?? ''}
                    </Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW} alt=''></img>
                    </Box>

                    <Typography color="text.primary" sx={{ fontWeight: 500 }}>Biometric Enrollment</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW_OPEN} alt='' ></img>
                    </Box>
                </Breadcrumbs>
            </Menu>

            <Container sx={{
                display: 'flex',
                flexDirection: 'column',
            }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                    gap: 2,
                    mb: 2.5,
                }} component='form'
                    autoComplete="off"
                    onSubmit={handleSubmit(handleViewData)}
                >
                    <TextField
                        sx={{ width: 238 }}
                        size='small'
                        label="Drive Name"
                        variant="outlined"
                        value={driveDetails['driveName']}
                        disabled
                    />

                    <TextField
                        sx={{ width: 238 }}
                        size='small'
                        id='rollNo'
                        name='rollNo'
                        className='form-field'
                        label={
                            <>
                                <Box component='span'>Roll No</Box>
                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                            </>
                        }
                        variant='outlined'
                        {...register('rollNo')}
                        error={Boolean(errors.rollNo)}
                        helperText={errors.rollNo?.message}
                        onBlur={() => trigger('rollNo')}
                    />
                    <RadiusButton sx={{
                        fontWeight: 500,
                    }} color='primary'
                        type="submit"
                        variant='contained'
                        // disabled={biometricMethods.length === 0}
                    >
                        View Data
                    </RadiusButton>
                    <Typography variant='subtitle' sx={{
                        fontWeight: 500,
                        alignSelf: 'center',
                        ml: 'auto'
                    }}>
                        Biometric Verified Candidates: &nbsp;
                        <Box component='span' sx={{ fontSize: 20, color: 'primary.main' }}>
                            {biometricVerifiedCandidatesCount}
                        </Box>
                    </Typography>
                </Box>
                <Box sx={{
                    flex: 1,
                    bgcolor: 'background.base',
                    borderRadius: 2,
                    p: 2
                }}>
                    {
                        candidateDetails
                            ? <Box sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                flexWrap: 'wrap',
                                gap: 2,
                                ml: 2,
                                mb: 3
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                    minWidth: 300,
                                    maxWidth: 500,
                                    flex: 1,
                                }}>
                                    <Typography variant='h6'>
                                        Name: &nbsp;
                                        <Box component='span' sx={{ color: 'primary.main', textTransform: 'capitalize' }}>
                                            {candidateDetails?.candidateName}
                                        </Box>
                                    </Typography>
                                    <Typography variant='h6'>
                                        DOB: &nbsp;
                                        <Box component='span' sx={{ color: 'primary.main', textTransform: 'capitalize' }}>
                                            {candidateDetails?.dob?.format('DD-MM-YYYY')}
                                        </Box>
                                    </Typography>
                                    <Typography variant='h6'>
                                        Seat No: &nbsp;
                                        <Box component='span' sx={{ color: 'primary.main', textTransform: 'capitalize' }}>
                                            {candidateDetails?.seatNo || 'Not Assigned'}
                                        </Box>
                                    </Typography>
                                    <Typography variant='h6'>
                                        Course: &nbsp;
                                        <Box component='span' sx={{ color: 'primary.main', textTransform: 'capitalize' }}>
                                            {candidateDetails?.courseName}
                                        </Box>
                                    </Typography>
                                    <Typography variant='h6'>
                                        Batch: &nbsp;
                                        <Box component='span' sx={{ color: 'primary.main', textTransform: 'capitalize' }}>
                                            {candidateDetails?.batchName}
                                        </Box>
                                    </Typography>
                                    <Typography variant='h6'>
                                        Exam Date: &nbsp;
                                        <Box component='span' sx={{ color: 'primary.main', textTransform: 'capitalize' }}>
                                            {candidateDetails?.examDate?.format('DD-MM-YYYY')}
                                        </Box>
                                    </Typography>
                                    <Typography variant='h6'>
                                        Venue: &nbsp;
                                        <Box component='span' sx={{ color: 'primary.main', textTransform: 'capitalize' }}>
                                            {candidateDetails?.hallName}
                                        </Box>
                                    </Typography>
                                    <Typography variant='h6'>
                                        Captured Fingers: &nbsp;
                                        <Box component='span' sx={{ color: 'primary.main', textTransform: 'capitalize' }}>
                                            {capturedThumbNames.join(', ') || 'None'}
                                        </Box>
                                    </Typography>
                                    <Typography variant='h6'>
                                        Verified Fingers: &nbsp;
                                        <Box component='span' sx={{ color: 'primary.main', textTransform: 'capitalize' }}>
                                            {verifiedThumbNames.join(', ') || 'None'}
                                        </Box>
                                    </Typography>
                                </Box>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: 2,
                                    flex: 1,
                                }}>
                                    <Box sx={{
                                        width: 170,
                                        height: 240,
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        boxShadow: 1,
                                        backgroundColor: 'grey.100',
                                    }}>
                                        {candidateDetails?.signedPhotoPath
                                            ? <img src={candidateDetails?.signedPhotoPath} alt='Candidate' style={{
                                                width: 'inherit',
                                                height: 'inherit',
                                                objectFit: 'cover',
                                                objectPosition: 'center',
                                            }} />
                                            : <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <Typography variant='h6' color='text.secondary'>Photo</Typography>
                                            </Box>}
                                    </Box>
                                    {
                                        biometricMethods.includes('PHOTO')
                                            ? <Box sx={{
                                                width: 170,
                                                height: 240,
                                                borderRadius: 2,
                                                overflow: 'hidden',
                                                boxShadow: 1,
                                                backgroundColor: 'grey.100',
                                            }}>
                                                {candidateDetails?.signedCapturedPhotoPath
                                                    ? <img src={candidateDetails.signedCapturedPhotoPath} alt='Candidate' style={{
                                                        width: 'inherit',
                                                        height: 'inherit',
                                                        objectFit: 'cover',
                                                        objectPosition: 'center',
                                                    }} />
                                                    : <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <Typography variant='h6' sx={{
                                                            color: 'text.secondary',
                                                            textAlign: 'center',
                                                        }}>Captured <br /> Photo</Typography>
                                                    </Box>}
                                            </Box>
                                            : null
                                    }

                                </Box>
                            </Box>
                            : <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 300,
                            }}>
                                <Typography variant='h6' color='text.secondary'>Candidate data not found</Typography>
                            </Box>
                    }

                    {
                        candidateDetails
                            ? <Box sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: 1,
                                alignItems: 'center',
                            }}>
                                {biometricMethods.includes('THUMB_IMPRESSION')
                                    ? isVerified
                                        ? <RadiusButton sx={{
                                            fontWeight: 500,
                                        }}
                                            color="success"
                                            variant='contained'
                                            size='large'
                                        >Verified</RadiusButton>
                                        : <RadiusButton sx={{
                                            fontWeight: 500,
                                            backgroundColor: 'grey.300',
                                        }}
                                            color="grey "
                                            variant='contained'
                                            size='large'
                                        >Not Verified</RadiusButton>
                                    : null
                                }
                                {biometricMethods.includes('THUMB_IMPRESSION')
                                    && <RadiusButton sx={{
                                        fontWeight: 500,
                                    }}
                                        color="secondary"
                                        variant='contained'
                                        size='large'
                                        onClick={handleThumbModalOpen}
                                    >Verify Biometric</RadiusButton>
                                }
                                {biometricMethods.includes('IRIS_SCAN')
                                    && <RadiusButton sx={{
                                        fontWeight: 500,
                                    }}
                                        color="secondary"
                                        variant='contained'
                                        size='large'
                                    // onClick={ }
                                    >Verify Iris Scan</RadiusButton>
                                }
                            </Box>
                            : null
                    }
                </Box>

                <Modal
                    hideBackdrop={true}
                    className='backdrop'
                    open={thumbModal}
                    onClose={handleThumbModalClose}
                >
                    <Box className='modal-container' sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                        width: 680,
                        px: 2,
                        py: 4,
                    }}>
                        <Typography variant="h5" component="h5">Verify Biometric</Typography>

                        <Box sx={{ width: '100%' }}
                            component='form'
                            autoComplete='off'
                        >
                            <FormControl className='form-field' sx={{
                                width: '100%',
                                mb: 2,
                            }}>
                                <InputLabel id="thumbLabel">
                                    Select Finger
                                    <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                </InputLabel>
                                <Select
                                    labelId="thumbLabel"
                                    id="thumb"
                                    label='Select Finger'
                                    margin='none'
                                    name='thumb'
                                    value={thumbId}
                                    onFocus={() => setThumbIdTouched(true)}
                                    onChange={(e, event) => {
                                        setThumbId(event.props.value);
                                        if (event.props.value !== 'OTHER') {
                                            setThumbRemarks('');
                                            setThumbRemarksTouched(false);
                                        }
                                        const thumb = capturedThumbs.find(thumb => thumb.thumbKey === e.target.value);
                                        setSelectedThumb(thumb);
                                    }}
                                >
                                    {
                                        capturedThumbs.map(
                                            (thumb) => <MenuItem key={thumb.thumbKey} value={thumb.thumbKey}>
                                                {thumb.thumbName}
                                                {thumb.isVerified
                                                    && <img className='thumb-captured-status' src={THUMB_VERIFIED_ICON} alt=''></img>
                                                }
                                            </MenuItem>
                                        )
                                    }
                                </Select>
                                {thumbErrors.thumb && (
                                    <FormHelperText sx={{ color: 'error.main' }}>{thumbErrors.thumb}</FormHelperText>
                                )}
                            </FormControl>

                            <TextareaAutosize
                                className='remarks-input'
                                style={{ width: '100%' }}
                                minRows={4}
                                maxRows={9}
                                value={thumbRemarks}
                                placeholder="Enter remarks here"
                                disabled={thumbId !== 'OTHER' ? true : false}
                                onFocus={() => {
                                    setThumbRemarksTouched(true);
                                }}
                                onChange={(e) => {
                                    setThumbRemarks(e.target.value);
                                }}
                            />
                            {thumbErrors.remarks && (
                                <FormHelperText sx={{ color: 'error.main' }}>{thumbErrors.remarks}</FormHelperText>
                            )}
                        </Box>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 2,
                        }}>
                            <LoadingButton
                                color="primary"
                                sx={{
                                    borderRadius: '46px',
                                    fontWeight: 500,
                                    textTransform: 'none'
                                }}
                                size='large'
                                variant='contained'
                                loadingPosition='center'
                                // loading={isVerifyingThumb}
                                onClick={handleSaveVerifyThumb}
                            >Save</LoadingButton>
                            {
                                thumbId !== 'OTHER'
                                    ? <LoadingButton
                                        color="primary"
                                        sx={{
                                            borderRadius: '46px',
                                            fontWeight: 500,
                                            textTransform: 'none'
                                        }}
                                        size='large'
                                        variant='contained'
                                        loadingPosition='center'
                                        disabled={selectedThumb?.key === 'NONE'}
                                        loading={isVerifyingThumb}
                                        onClick={handleVerifyThumb}
                                    >Verify Biometric</LoadingButton>
                                    : null
                            }
                            <RadiusButton sx={{
                                fontWeight: 500,
                            }}
                                color="secondary"
                                variant='contained'
                                size='large'
                                onClick={handleThumbModalClose}
                            >Cancel</RadiusButton>

                        </Box>
                    </Box>
                </Modal>

            </Container>

        </MainContainer >
    );
}
